// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the presenter's camera and microphone on screen
 * (`docs/SHARED_AR_PLAN.md`), on both sides of a meeting:
 *
 *   - the presenter gets "Go live" under the meeting line, then a small
 *     preview of what everyone sees, with the microphone and camera
 *     switches, a choice of devices, and Stop. The LIVE badge turns amber
 *     while the connection is holding the picture back. A connection that
 *     drops, a room that forgot the presenter, and a camera or microphone
 *     that ends (a phone putting the page away, a headset unplugged) are
 *     each mended without the presenter doing anything;
 *   - everyone else gets the presenter in a floating window at the bottom
 *     right, which can be folded away or switched to sound only, and
 *     which connects again by itself when this viewer's connection drops.
 *
 * Browsers will not start sound on a page nobody has touched, so the
 * window starts muted, with one button to hear the presenter; the picture
 * plays at once. The presenter's own preview is always muted.
 *
 * Driven by the room's status (`RoomStatus.mediaRev`, `ticket`): a new
 * revision means pull again, null means the presenter stopped.
 *
 * Mounted in the WebXR overlay root, so it is over the camera picture in
 * handheld AR too. Lives in `src/ui/` so `check:i18n-strings` covers it.
 */

import { t } from '../i18n'
import type { RoomStatus } from '../services/roomClient'
import {
  goLive,
  listDevices,
  MeetingMediaError,
  openCameraAndMic,
  openDevice,
  openMic,
  preferredDevices,
  rememberDevice,
  stopLive,
  watch,
  type LiveHandle,
  type MicSlot,
  type Quality,
  type WatchHandle,
} from '../services/meetingMedia'
import { isVrActive, notifyXr } from '../services/vrSession'

/**
 * AYNI (Quest review A5, 2026-11-03): the meeting's sound (the presenter,
 * the globe drivers' microphones), switchable from outside the page. A
 * headset can't reach the page's Listen buttons, so the dock's speaker
 * button drives this while there is something to hear. It stays off until
 * someone asks: students in the teacher's room would otherwise hear the
 * teacher twice, a moment apart.
 */
interface SoundPart {
  /** A stream is attached (something to hear). */
  present(): boolean
  on(): boolean
  set(on: boolean): void
}
const soundParts = new Set<SoundPart>()

/** The meeting's sound: 'on' or 'off' while there is something to hear, else null. */
export function meetingSound(): 'on' | 'off' | null {
  const here = [...soundParts].filter((p) => p.present())
  if (here.length === 0) return null
  return here.some((p) => p.on()) ? 'on' : 'off'
}

/** Turn the meeting's sound on or off, as the page's Listen buttons do (call from a press). */
export function setMeetingSound(on: boolean): void {
  for (const p of soundParts) p.set(on)
}

/** In a headset or phone session, say that the presenter is live and how to hear them. */
function tellXrPresenterLive(): void {
  if (!isVrActive()) return
  notifyXr(t(meetingSound() === 'on' ? 'vr.notice.meetingSound.on' : 'vr.notice.meetingSound.off'))
}

/** On entering a session (main.ts onSessionStart): the same, when the presenter is already live. */
export function tellXrMeetingSound(): void {
  if (meetingSound() !== null) tellXrPresenterLive()
}

/**
 * The microphone this device may put on: the globe it drives, or (AYNI,
 * owner 2026-10-12) `spot` while it holds the presenter's main globe and
 * the presenter has not taken over; null otherwise.
 */
export function micSlotOf(status: RoomStatus | null): MicSlot | null {
  if (!status?.connected || !status.meeting) return null
  if (status.driving !== null) return status.driving
  return status.spotlight?.me && !status.spotlight.override ? 'spot' : null
}

/** Whether the room says microphone `slot` is live. */
const micLive = (status: RoomStatus, slot: MicSlot): boolean =>
  slot === 'spot' ? status.spotLive !== null : status.driversLive.some((d) => d.slot === slot)

export interface MeetingMediaOptions {
  code: string
  /** The presenter's signed link token, when this page has one. */
  token: string | null
}

/** How often the presenter's connection is read, and how many readings to ignore while it ramps up. */
const QUALITY_EVERY_MS = 3000
const QUALITY_WARMUP_READINGS = 2

/** The wait before connecting again: doubling from two seconds to fifteen; a refused-for-too-many waits the longest. */
export function retryDelay(attempt: number, tooMany = false): number {
  return tooMany ? 15_000 : Math.min(2000 * 2 ** attempt, 15_000)
}

/**
 * AYNI (load test, 2026-10-17): when the presenter or a driver goes live,
 * every viewer pulls at once. Realtime took 500 viewers cleanly arriving
 * about 27 a second, and failed more of them arriving about 100 a second,
 * so past the 50 meetings always held, each viewer waits a random moment
 * in a window that keeps the meeting near PULLS_PER_SECOND: 500 people
 * spread over 20 seconds.
 */
const SPREAD_FREE_PEOPLE = 50
const PULLS_PER_SECOND = 25
const SPREAD_MAX_MS = 20_000

/** The random wait before a viewer's pull in a meeting of `people`. */
export function pullSpread(people: number, random: () => number = Math.random): number {
  if (people <= SPREAD_FREE_PEOPLE) return 0
  return Math.floor(random() * Math.min((people / PULLS_PER_SECOND) * 1000, SPREAD_MAX_MS))
}

/** Mount both sides; each shows itself only for its seat. Returns the teardown. */
export function mountMeetingMedia(
  root: HTMLElement,
  subscribe: (listener: (status: RoomStatus) => void) => () => void,
  opts: MeetingMediaOptions,
): () => void {
  const presenter = presenterControls(root, opts)
  const viewer = presenterWindow(root, opts)
  // AYNI (multi-globe plan, phase 3): a globe's driver may put its microphone on; everyone else hears the drivers.
  const driver = driverControls(root, opts)
  const drivers = driversAudio(root, opts)
  const unsubscribe = subscribe((status) => {
    const isPresenter = status.meeting && status.seat === 'presenter'
    presenter.update(isPresenter ? status : null)
    viewer.update(status.meeting && !isPresenter ? status : null)
    driver.update(micSlotOf(status) !== null ? status : null)
    drivers.update(status.meeting ? status : null)
  })
  return () => {
    unsubscribe()
    presenter.dispose()
    viewer.dispose()
    driver.dispose()
    drivers.dispose()
  }
}

// ------------------------------------------------------------------ drivers

/**
 * AYNI (multi-globe plan, phase 3): the microphone of a globe's driver.
 * Shown to a device given a globe: one button puts the microphone on
 * (the consent is in the words beside it; the browser asks too), then a
 * badge, Mute and Stop. It stops when the globe is given or taken back.
 * AYNI (owner, 2026-10-12): the participant handed the presenter's main
 * globe gets it too; it stops when the presenter takes over for a moment;
 * and when the presenter or a moderator turns it off, the room forgets it
 * and this device stops sending, and says so.
 */
function driverControls(root: HTMLElement, opts: MeetingMediaOptions) {
  const box = document.createElement('div')
  box.className = 'meeting-driver-mic hidden'
  box.setAttribute('role', 'region')
  box.setAttribute('aria-label', t('meeting.driverMic.aria'))
  const start = button('meeting-live-start', t('meeting.driverMic.start'))
  const consent = document.createElement('p')
  consent.className = 'meeting-driver-mic-consent'
  consent.textContent = t('meeting.driverMic.consent')
  const message = document.createElement('p')
  message.className = 'meeting-live-message'
  message.setAttribute('role', 'status')
  const live = document.createElement('div')
  live.className = 'meeting-live-on hidden'
  const badge = document.createElement('span')
  badge.className = 'meeting-live-badge'
  badge.textContent = t('meeting.driverMic.live')
  const mute = button('', t('meeting.driverMic.mute'))
  const stop = button('', t('meeting.driverMic.stop'))
  const row = document.createElement('div')
  row.className = 'meeting-live-buttons'
  row.append(mute, stop)
  live.append(badge, row)
  box.append(start, consent, live, message)
  root.appendChild(box)

  let slot: MicSlot | null = null
  let ticket: string | null = null
  let stream: MediaStream | null = null
  let handle: LiveHandle | null = null
  let busy = false
  /** The room has said this microphone is live: from then on, its absence means someone turned it off. */
  let seenLive = false

  const showLive = (on: boolean): void => {
    live.classList.toggle('hidden', !on)
    start.classList.toggle('hidden', on)
    consent.classList.toggle('hidden', on)
  }

  async function start_(): Promise<void> {
    if (busy || slot === null || !ticket) return
    busy = true
    start.disabled = true
    message.textContent = ''
    try {
      stream = await openMic()
      seenLive = false
      handle = await goLive(opts.code, '', stream, undefined, undefined, { kind: 'driver', slot, ticket })
      handle.onDrop(() => end(false))
      showLive(true)
      mute.textContent = t('meeting.driverMic.mute')
    } catch (err) {
      const reason = err instanceof MeetingMediaError ? err.reason : 'unavailable'
      message.textContent = t(reason === 'denied' ? 'meeting.driverMic.denied' : 'meeting.driverMic.failed')
      end(false)
    } finally {
      busy = false
      start.disabled = false
    }
  }

  function end(tellRoom: boolean): void {
    const was = !!handle
    handle?.close()
    handle = null
    for (const track of stream?.getTracks() ?? []) track.stop()
    stream = null
    showLive(false)
    if (tellRoom && was && slot !== null && ticket) void stopLive(opts.code, '', undefined, { kind: 'driver', slot, ticket })
  }

  start.addEventListener('click', () => void start_())
  stop.addEventListener('click', () => end(true))
  mute.addEventListener('click', () => {
    const track = stream?.getAudioTracks()[0]
    if (!track) return
    track.enabled = !track.enabled
    mute.textContent = t(track.enabled ? 'meeting.driverMic.mute' : 'meeting.driverMic.unmute')
    mute.setAttribute('aria-pressed', String(!track.enabled))
  })

  return {
    update(status: RoomStatus | null) {
      const next = micSlotOf(status)
      ticket = status?.ticket ?? null
      box.classList.toggle('hidden', next === null)
      // The globe went back, or another one came: the microphone goes with it (the room has already forgotten it).
      if (next !== slot && handle) end(false)
      slot = next
      // Turned off by the presenter or a moderator: the room no longer lists it. The room says
      // "live" while this device is still connecting (before goLive returns), so that counts too
      // (live check, 2026-10-13: Mic off never reached the driver).
      if ((handle || busy) && status && slot !== null && micLive(status, slot)) seenLive = true
      else if (handle && !busy && status && slot !== null && seenLive) {
        end(false)
        message.textContent = t('meeting.driverMic.turnedOff')
      }
    },
    dispose() {
      end(false)
      box.remove()
    },
  }
}

/**
 * AYNI (multi-globe plan, phase 3): the drivers' microphones, for everyone
 * else — one sound element per driver live, muted until "Listen" (the
 * browser plays sound only after a tap), pulled again on a new revision.
 */
function driversAudio(root: HTMLElement, opts: MeetingMediaOptions) {
  const box = document.createElement('div')
  box.className = 'meeting-drivers-audio hidden'
  box.setAttribute('role', 'region')
  box.setAttribute('aria-label', t('meeting.drivers.audio.label'))
  const label = document.createElement('span')
  label.className = 'meeting-drivers-audio-label'
  label.textContent = t('meeting.drivers.audio.label')
  const listen = button('meeting-presenter-listen', t('meeting.drivers.audio.listen'))
  const list = document.createElement('div')
  list.className = 'meeting-drivers-audio-list'
  box.append(label, list, listen)
  root.appendChild(box)

  /** Per globe (and `spot`, the main globe's driver): what is pulled, and the element playing it. */
  const streams = new Map<MicSlot, { rev: number; handle: WatchHandle | null; audio: HTMLAudioElement; name: HTMLSpanElement; pulling: boolean }>()
  let ticket: string | null = null
  let myDriving: MicSlot | null = null
  let spotName: string | null = null
  let unmuted = false
  let people = 0

  const render = (): void => {
    box.classList.toggle('hidden', streams.size === 0)
    listen.classList.toggle('hidden', unmuted || streams.size === 0)
  }

  function drop(slot: MicSlot): void {
    const s = streams.get(slot)
    if (!s) return
    s.handle?.close()
    s.audio.srcObject = null
    s.audio.remove()
    s.name.remove()
    streams.delete(slot)
    render()
  }

  async function pull(slot: MicSlot, rev: number): Promise<void> {
    if (!ticket) return
    let s = streams.get(slot)
    if (!s) {
      const audio = document.createElement('audio')
      audio.autoplay = true
      audio.muted = !unmuted
      const name = document.createElement('span')
      name.className = 'meeting-drivers-audio-one'
      name.textContent = slot === 'spot'
        ? spotName ? t('meeting.drivers.audio.spot', { name: spotName }) : t('meeting.drivers.audio.spotSomeone')
        : t('meeting.drivers.audio.one', { n: slot + 1 })
      list.append(name, audio)
      s = { rev, handle: null, audio, name, pulling: false }
      streams.set(slot, s)
      render()
    }
    s.rev = rev
    if (s.pulling) return
    s.pulling = true
    try {
      const wait = pullSpread(people)
      if (wait > 0) {
        await new Promise((resolve) => setTimeout(resolve, wait))
        if (streams.get(slot) !== s || s.rev !== rev || !ticket) return
      }
      const handle = await watch(opts.code, ticket, { audioOnly: true, slot })
      const now = streams.get(slot)
      if (!now || now.rev !== rev) {
        handle.close()
        return
      }
      now.handle?.close()
      now.handle = handle
      handle.onDrop(() => {
        if (streams.get(slot)?.handle === handle) void pull(slot, rev)
      })
      now.audio.srcObject = handle.stream
      void now.audio.play()?.catch(() => undefined)
    } catch {
      // The driver may have stopped meanwhile; the next media message says.
    } finally {
      // Dropped and listed again meanwhile: the new entry runs its own pull.
      if (streams.get(slot) === s) {
        s.pulling = false
        // A newer revision arrived while this pull waited or ran: pull that one.
        if (s.rev !== rev) void pull(slot, s.rev)
      }
    }
  }

  const setSound = (on: boolean): void => {
    unmuted = on
    for (const s of streams.values()) {
      s.audio.muted = !on
      if (on) void s.audio.play()?.catch(() => undefined)
    }
    render()
  }
  listen.addEventListener('click', () => setSound(true))
  const sound: SoundPart = { present: () => streams.size > 0, on: () => unmuted, set: setSound }
  soundParts.add(sound)

  return {
    update(status: RoomStatus | null) {
      ticket = status?.ticket ?? null
      people = status?.count ?? 0
      myDriving = micSlotOf(status)
      spotName = status?.spotlight?.name ?? null
      const live: { slot: MicSlot; rev: number }[] = status?.connected
        ? [...status.driversLive, ...(status.spotLive !== null ? [{ slot: 'spot' as const, rev: status.spotLive }] : [])]
        : []
      for (const slot of [...streams.keys()]) if (!live.some((d) => d.slot === slot)) drop(slot)
      for (const d of live) {
        // One's own microphone is not pulled back.
        if (d.slot === myDriving) continue
        const s = streams.get(d.slot)
        if (!s || s.rev !== d.rev) void pull(d.slot, d.rev)
      }
      render()
    },
    dispose() {
      soundParts.delete(sound)
      for (const slot of [...streams.keys()]) drop(slot)
      box.remove()
    },
  }
}

function button(className: string, text = ''): HTMLButtonElement {
  const b = document.createElement('button')
  b.type = 'button'
  if (className) b.className = className
  b.textContent = text
  // Taps on the overlay must not reach the AR session as a select.
  b.addEventListener('beforexrselect', (ev) => ev.preventDefault())
  return b
}

// ---------------------------------------------------------------- presenter

function presenterControls(root: HTMLElement, opts: MeetingMediaOptions) {
  const box = document.createElement('div')
  box.className = 'meeting-live hidden'
  const start = button('meeting-live-start', t('meeting.live.start'))
  const message = document.createElement('p')
  message.className = 'meeting-live-message'
  message.setAttribute('role', 'status')

  const live = document.createElement('div')
  live.className = 'meeting-live-on hidden'
  const preview = document.createElement('video')
  preview.muted = true
  preview.playsInline = true
  preview.autoplay = true
  preview.setAttribute('playsinline', '')
  preview.setAttribute('aria-label', t('meeting.live.preview.aria'))
  const badge = document.createElement('span')
  badge.className = 'meeting-live-badge'
  badge.textContent = t('meeting.live.badge')
  const mic = button('')
  const camera = button('')
  const devices = button('', t('meeting.live.devices'))
  devices.setAttribute('aria-expanded', 'false')
  const stop = button('', t('meeting.live.stop'))
  const row = document.createElement('div')
  row.className = 'meeting-live-buttons'
  row.append(mic, camera, devices, stop)

  const picker = document.createElement('div')
  picker.className = 'meeting-live-devices hidden'
  const cameraSelect = document.createElement('select')
  const micSelect = document.createElement('select')
  for (const [select, key] of [[cameraSelect, 'meeting.live.devices.camera'], [micSelect, 'meeting.live.devices.mic']] as const) {
    const label = document.createElement('label')
    label.append(t(key), select)
    picker.append(label)
  }
  devices.setAttribute('aria-controls', (picker.id = 'meeting-live-devices'))

  live.append(preview, badge, row, picker)
  box.append(start, live, message)
  root.appendChild(box)

  let stream: MediaStream | null = null
  let handle: LiveHandle | null = null
  /** The presenter pressed Go live and has not pressed Stop. */
  let wantLive = false
  let busy = false
  /** The room had our media; once it says none while we are live, publish again. */
  let roomHadMedia = false
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let qualityTimer: ReturnType<typeof setInterval> | null = null
  let shownQuality: Quality = 'good'
  let lastReading: Quality | null = null
  let readings = 0
  /** What the message line is saying, so a quality notice never clears an error. */
  let messageKind: 'none' | 'quality' | 'reconnecting' | 'error' = 'none'

  const say = (kind: typeof messageKind, text: string): void => {
    messageKind = text ? kind : 'none'
    message.textContent = text
  }

  const labels = (): void => {
    const audio = stream?.getAudioTracks()[0]
    const video = stream?.getVideoTracks()[0]
    mic.textContent = t(audio && !audio.enabled ? 'meeting.live.mic.unmute' : 'meeting.live.mic.mute')
    mic.setAttribute('aria-pressed', String(!!audio && !audio.enabled))
    camera.textContent = t(video && !video.enabled ? 'meeting.live.camera.on' : 'meeting.live.camera.off')
    camera.setAttribute('aria-pressed', String(!!video && !video.enabled))
  }
  const showLive = (on: boolean): void => {
    start.classList.toggle('hidden', on)
    live.classList.toggle('hidden', !on)
    if (!on) {
      picker.classList.add('hidden')
      devices.setAttribute('aria-expanded', 'false')
    }
  }

  function showQuality(q: Quality): void {
    shownQuality = q
    badge.classList.toggle('weak', q === 'weak')
    badge.classList.toggle('poor', q === 'poor')
    if (q === 'good') {
      badge.removeAttribute('title')
      if (messageKind === 'quality') say('none', '')
    } else {
      const text = t(q === 'weak' ? 'meeting.live.quality.weak' : 'meeting.live.quality.poor')
      badge.title = text
      if (messageKind === 'none' || messageKind === 'quality') say('quality', text)
    }
  }

  function startQuality(): void {
    stopQuality()
    readings = 0
    lastReading = null
    showQuality('good')
    qualityTimer = setInterval(() => {
      const current = handle
      if (!current) return
      void current.quality().then((q) => {
        if (current !== handle) return
        // A new connection starts short of bandwidth and climbs, so its
        // first readings are ignored; and one reading is noise, so what is
        // shown changes on two alike in a row.
        if (++readings <= QUALITY_WARMUP_READINGS) return
        if (q === lastReading && q !== shownQuality) showQuality(q)
        lastReading = q
      }, () => undefined)
    }, QUALITY_EVERY_MS)
  }

  function stopQuality(): void {
    if (qualityTimer !== null) clearInterval(qualityTimer)
    qualityTimer = null
  }

  /** Publish the stream, replacing any earlier publication. */
  async function connect(): Promise<void> {
    if (!stream || !opts.token) throw new MeetingMediaError('unavailable')
    const next = await goLive(opts.code, opts.token, stream)
    if (!wantLive) {
      next.close()
      return
    }
    handle?.close()
    handle = next
    roomHadMedia = false
    next.onDrop(() => {
      if (handle === next) reconnect()
    })
    startQuality()
  }

  /** Publish again, at once the first time and then more slowly. */
  function reconnect(attempt = 0, tooMany = false): void {
    if (!wantLive || reconnectTimer !== null) return
    say('reconnecting', t('meeting.live.reconnecting'))
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      if (!wantLive) return
      connect().then(
        () => {
          if (messageKind === 'reconnecting') say('none', '')
        },
        (err: unknown) => {
          const reason = err instanceof MeetingMediaError ? err.reason : 'unavailable'
          if (reason === 'not_presenter') {
            end(false)
            say('error', t('meeting.live.error.unavailable'))
            return
          }
          reconnect(attempt + 1, reason === 'too_many')
        },
      )
    }, attempt === 0 ? 0 : retryDelay(attempt - 1, tooMany))
  }

  /** A camera or microphone that ended (put away, unplugged): open it again and put it in place. */
  async function reopen(kind: 'audio' | 'video', deviceId: string | null = preferredDevices()[kind]): Promise<void> {
    if (!wantLive || !stream) return
    let track: MediaStreamTrack
    try {
      track = await openDevice(kind, deviceId).catch(() => openDevice(kind, null))
    } catch {
      say('error', t('meeting.live.error.device'))
      return
    }
    await swapTrack(track)
  }

  async function swapTrack(track: MediaStreamTrack): Promise<void> {
    if (!stream) {
      track.stop()
      return
    }
    const old = track.kind === 'video' ? stream.getVideoTracks()[0] : stream.getAudioTracks()[0]
    track.enabled = old ? old.enabled : true
    try {
      await handle?.replaceTrack(track)
    } catch {
      track.stop()
      say('error', t('meeting.live.error.device'))
      return
    }
    if (old) {
      stream.removeTrack(old)
      old.stop()
    }
    stream.addTrack(track)
    listenForEnd(track)
    if (track.kind === 'video') {
      preview.srcObject = stream
      void preview.play()?.catch(() => undefined)
    }
    labels()
  }

  function listenForEnd(track: MediaStreamTrack): void {
    track.addEventListener('ended', () => {
      // Our own stop() fires no 'ended'; this is the browser or the device.
      if (stream?.getTracks().includes(track)) void reopen(track.kind as 'audio' | 'video')
    })
  }

  const onVisible = (): void => {
    if (document.visibilityState !== 'visible' || !wantLive || !stream) return
    for (const track of stream.getTracks()) {
      if (track.readyState === 'ended') void reopen(track.kind as 'audio' | 'video')
    }
  }
  document.addEventListener('visibilitychange', onVisible)

  async function goLiveNow(): Promise<void> {
    if (busy || !opts.token) return
    busy = true
    wantLive = true
    start.disabled = true
    say('none', t('meeting.live.starting'))
    try {
      if (!stream) {
        stream = await openCameraAndMic()
        stream.getTracks().forEach(listenForEnd)
      }
      await connect()
      preview.srcObject = stream
      void preview.play()?.catch(() => undefined)
      showLive(true)
      labels()
      say('none', '')
    } catch (err) {
      const reason = err instanceof MeetingMediaError ? err.reason : 'unavailable'
      say('error', t(reason === 'denied' ? 'meeting.live.error.denied' : 'meeting.live.error.unavailable'))
      if (!handle) end(false)
    } finally {
      busy = false
      start.disabled = false
    }
  }

  function end(tellRoom: boolean): void {
    wantLive = false
    if (reconnectTimer !== null) clearTimeout(reconnectTimer)
    reconnectTimer = null
    stopQuality()
    handle?.close()
    handle = null
    for (const track of stream?.getTracks() ?? []) track.stop()
    stream = null
    preview.srcObject = null
    roomHadMedia = false
    showQuality('good')
    showLive(false)
    if (messageKind !== 'error') say('none', '')
    if (tellRoom && opts.token) void stopLive(opts.code, opts.token)
  }

  async function fillPicker(): Promise<void> {
    const found = await listDevices()
    const current = {
      video: stream?.getVideoTracks()[0]?.getSettings?.().deviceId ?? null,
      audio: stream?.getAudioTracks()[0]?.getSettings?.().deviceId ?? null,
    }
    for (const [select, kind] of [[cameraSelect, 'video'], [micSelect, 'audio']] as const) {
      select.replaceChildren(
        ...found[kind].map((d, i) => {
          const option = document.createElement('option')
          option.value = d.deviceId
          option.textContent = d.label || t('meeting.live.devices.unnamed', { n: i + 1 })
          option.selected = d.deviceId === current[kind]
          return option
        }),
      )
      select.disabled = found[kind].length < 2
    }
  }

  start.addEventListener('click', () => void goLiveNow())
  stop.addEventListener('click', () => end(true))
  mic.addEventListener('click', () => {
    const track = stream?.getAudioTracks()[0]
    if (track) track.enabled = !track.enabled
    labels()
  })
  camera.addEventListener('click', () => {
    const track = stream?.getVideoTracks()[0]
    if (track) track.enabled = !track.enabled
    labels()
  })
  devices.addEventListener('click', () => {
    const open = picker.classList.toggle('hidden') === false
    devices.setAttribute('aria-expanded', String(open))
    if (open) void fillPicker()
  })
  for (const [select, kind] of [[cameraSelect, 'video'], [micSelect, 'audio']] as const) {
    select.addEventListener('change', () => {
      const id = select.value
      void openDevice(kind, id).then(
        async (track) => {
          await swapTrack(track)
          rememberDevice(kind, id)
        },
        () => say('error', t('meeting.live.error.device')),
      )
    })
  }

  return {
    update(status: RoomStatus | null) {
      box.classList.toggle('hidden', status === null || !opts.token)
      if (!status) {
        if (wantLive) end(false)
        return
      }
      if (handle && status.connected) {
        if (status.mediaRev !== null) roomHadMedia = true
        else if (roomHadMedia) {
          // The room forgot us (our connection dropped and came back): again.
          roomHadMedia = false
          reconnect()
        }
      }
    },
    dispose() {
      document.removeEventListener('visibilitychange', onVisible)
      end(false)
      box.remove()
    },
  }
}

// ----------------------------------------------------------------- everyone

function presenterWindow(root: HTMLElement, opts: MeetingMediaOptions) {
  const box = document.createElement('div')
  box.className = 'meeting-presenter hidden'
  box.setAttribute('role', 'region')
  box.setAttribute('aria-label', t('meeting.presenter.label'))
  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.autoplay = true
  video.setAttribute('playsinline', '')
  const label = document.createElement('span')
  label.className = 'meeting-presenter-label'
  const listen = button('meeting-presenter-listen', t('meeting.presenter.listen'))
  const mode = button('meeting-presenter-mode')
  const fold = button('meeting-presenter-fold')
  box.append(video, label, listen, mode, fold)
  root.appendChild(box)

  let current: WatchHandle | null = null
  /** The revision a headset or phone session was last told about (tellXrPresenterLive). */
  let toldRev: number | null = null
  let wantedRev: number | null = null
  let ticket: string | null = null
  let pulling = false
  /** Pull again once the pull under way is done (the mode changed meanwhile). */
  let pullAgain = false
  let folded = false
  let soundOnly = false
  let reconnecting = false
  let attempt = 0
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let people = 0

  const render = (): void => {
    box.classList.toggle('folded', folded)
    box.classList.toggle('sound-only', soundOnly)
    fold.textContent = folded ? '▴' : '–'
    fold.setAttribute('aria-label', t(folded ? 'meeting.presenter.show' : 'meeting.presenter.hide'))
    mode.textContent = t(soundOnly ? 'meeting.presenter.showVideo' : 'meeting.presenter.soundOnly')
    mode.setAttribute('aria-pressed', String(soundOnly))
    label.textContent = t(
      reconnecting ? 'meeting.presenter.reconnecting' : soundOnly ? 'meeting.presenter.soundOnlyLabel' : 'meeting.presenter.label',
    )
  }
  render()

  function clearRetry(): void {
    if (retryTimer !== null) clearTimeout(retryTimer)
    retryTimer = null
  }

  function close(): void {
    clearRetry()
    current?.close()
    current = null
    video.srcObject = null
    reconnecting = false
    attempt = 0
    box.classList.add('hidden')
    render()
  }

  /** Pull after `delay`, plus this viewer's share of the meeting's spread (a drop for one is often a drop for all). */
  function pullLater(delay: number): void {
    clearRetry()
    retryTimer = setTimeout(() => {
      retryTimer = null
      void pull()
    }, delay + pullSpread(people))
  }

  function retry(tooMany = false): void {
    pullLater(retryDelay(attempt++, tooMany))
  }

  /** A new revision: everyone pulls it, each at their moment. */
  function pullSpreadOut(): void {
    clearRetry()
    if (pullSpread(people) === 0) void pull()
    else pullLater(0)
  }

  async function pull(): Promise<void> {
    if (pulling) {
      pullAgain = true
      return
    }
    const rev = wantedRev
    const asSoundOnly = soundOnly
    if (rev === null || !ticket) return
    pulling = true
    try {
      const handle = await watch(opts.code, ticket, { audioOnly: asSoundOnly })
      if (wantedRev !== rev || soundOnly !== asSoundOnly) {
        handle.close()
        if (wantedRev === rev) pullAgain = true
        return
      }
      current?.close()
      current = handle
      handle.onDrop(() => {
        if (current !== handle) return
        reconnecting = true
        render()
        retry()
      })
      video.srcObject = handle.stream
      box.classList.remove('hidden')
      void video.play()?.catch(() => undefined)
      listen.classList.toggle('hidden', !video.muted)
      // Once per broadcast (not on every reconnection): a headset can't see this window.
      if (toldRev !== rev) {
        toldRev = rev
        tellXrPresenterLive()
      }
      reconnecting = false
      attempt = 0
      render()
    } catch (err) {
      if (wantedRev !== rev) return
      const reason = err instanceof MeetingMediaError ? err.reason : 'unavailable'
      // Not in the room: this connection's ticket is stale, and a new one comes with the room's next welcome.
      if (reason === 'not_in_room') return
      // The presenter may have stopped, a pull raced the publish, or the network is out: again, more slowly each time.
      retry(reason === 'too_many')
    } finally {
      pulling = false
      if (pullAgain) {
        pullAgain = false
        void pull()
      }
    }
  }

  const onOnline = (): void => {
    if (retryTimer !== null && wantedRev !== null) {
      clearRetry()
      void pull()
    }
  }
  window.addEventListener('online', onOnline)

  const setSound = (on: boolean): void => {
    video.muted = !on
    if (on) void video.play()?.catch(() => undefined)
    listen.classList.toggle('hidden', on || current === null)
  }
  listen.addEventListener('click', () => setSound(true))
  const sound: SoundPart = { present: () => current !== null, on: () => !video.muted, set: setSound }
  soundParts.add(sound)
  fold.addEventListener('click', () => {
    folded = !folded
    render()
  })
  mode.addEventListener('click', () => {
    soundOnly = !soundOnly
    render()
    if (wantedRev !== null) {
      clearRetry()
      attempt = 0
      void pull()
    }
  })

  return {
    update(status: RoomStatus | null) {
      const rev = status?.connected ? status.mediaRev : null
      ticket = status?.ticket ?? null
      people = status?.count ?? 0
      if (rev === wantedRev) return
      wantedRev = rev
      clearRetry()
      attempt = 0
      if (rev === null) close()
      else pullSpreadOut()
    },
    dispose() {
      soundParts.delete(sound)
      window.removeEventListener('online', onOnline)
      close()
      box.remove()
    },
  }
}
